import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { CurrencyPipe } from "@angular/common";
import { Program } from "../../../../settings/calc-plan-editor/model/program";
import {
  layoutProgram,
  TreeChart,
  TreeChartEdge,
  TreeChartNode
} from "../../../../settings/calc-plan-editor/plan-layout";
import {
  calculationSource,
  literalSource
} from "../../../../settings/calc-plan-editor/model/transient";
import {
  IAnswer,
  IFormField,
  makeControlForQuestion,
} from "../../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { BlockType } from "../../../../settings/calc-plan-editor/model/block";
import { CalcInput } from "../../../../settings/calc-plan-editor/model/calc-input";
import { AnswerTrace } from "../../../../../../swaggerServices";
import { ConfigStateService } from "../../../../config/config-state.service";
import { Tenant } from "../../../../../shared/types/tenant.enum";
import { ITaxReturn } from "../../model/i-tax-return";
import { PopulationRulesResolver } from "../../../../clients/client/populations/population-rules/population-rules-resolver.service";
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { IGroup } from "../subcategory";

declare var $: any;

const formatValue = value => {
  if (value instanceof Array) {
    const contents = value.map(formatValue).join(", ");
    return `[${contents}]`;
  } else {
    return value != null ? value : "null";
  }
};
const formatOutputCell = cell => {
  if (cell.value instanceof Array) {
    const contents = cell.value.map(formatValue).join(", ");
    return `[${contents}]`;
  } else {
    return cell.value != null ? cell.value : "null";
  }
};

@Component({
    selector: 'app-answer-trace',
    templateUrl: './answer-trace.component.html',
    styleUrls: ['./answer-trace.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AnswerTraceComponent implements OnInit, OnChanges {
  @Input() trace: AnswerTrace;
  @Input() question: IFormField;
  @Input() answer: IAnswer;
  @Input() taxReturn: ITaxReturn;
  @Input() subCategory: any;
  @Input() activeGroup: any;
  @ViewChild("chartContainer") chartContainer: ElementRef;
  @ViewChild('viewAnswer') viewAnswer: ElementRef;
  charts: TreeChart[] = [];
  selectedChart?: TreeChart;
  selectedNode?: TreeChartNode;
  finalCalcOutput?: string;
  traceNotAvailable?: boolean;
  gridSize: number = 120;
  blockSizes: Map<BlockType, {w: number, h: number}> = new Map<BlockType, {w: number, h: number}>([
    [BlockType.Start, {w: 54, h: 54}],
    [BlockType.End, {w: 54, h: 54}],
    [BlockType.Value, {w: 54, h: 54}],
    [BlockType.Decision, {w: 90, h: 90}],
    [BlockType.Function, {w: 96, h: 50}],
    [BlockType.Warning, {w: 70, h: 50}],
  ]);
  unresolvedInputText = 'This data was not used in the final calculation, the full block input is shown here.';
  unresolvedOutputText = 'This data was not used in the final calculation, the full block output is shown here.';
  BlockType = BlockType;
  showQuestionId: boolean;
  clientRules: any;
  group: any;
  subGroup: any;
  subCategoryName: string;
  isClientRulesViewModal: boolean;
  clientRulesCategory: string;
  categoryRules: any;
  inputQuestionId: number;

  constructor(private currencyPipe: CurrencyPipe,
              private config: ConfigStateService,
              private populationRulesResolver: PopulationRulesResolver,) { }

  ngOnInit() {
    this.showQuestionId = this.config.tenant !== Tenant.AU;
    this.populationRulesResolver.resolveForPopulation(this.taxReturn.populationId).subscribe(
      (data) => {
        this.clientRules = data;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.trace || changes.question || changes.answer) {
      this.charts = [];
      this.selectedChart = null;
      this.selectedNode = null;
      this.finalCalcOutput = null;
      this.traceNotAvailable = false;

      if (this.trace) {
        const trace: any = this.trace;
        const programTraces = JSON.parse(trace.trace);

        const blocks = new Map();
        trace.blocks.forEach(b => {
          blocks.set(b.id, b);
        });
        const sources = new Map();
        trace.sources.forEach(s => {
          sources.set(s.id.toString(), s);
        });
        programTraces.forEach(programTrace => {
          programTrace.forEach(x => {
            const filtered = x.instruction.inputs.filter(input =>
              input.source === "literal" || input.source === "calculation" || sources.has(input.source));
            if (filtered.length !== x.instruction.inputs.length) {
              x.instruction.inputs = filtered;
            }
          });
        });

        programTraces.forEach((programTrace, i) => {
          if (programTrace.length === 0) {
            return;
          }
          const instructionTree = [];
          let curTree = instructionTree;
          programTrace.forEach(x => {
            curTree.push(x.instruction);
            // set up the instructions so all decisions go to "yes", a bit of a hack to generate
            // a chart that always goes to the right
            if (x.instruction.yes || x.instruction.no) {
              const newTree = [];
              x.instruction.yes = newTree;
              x.instruction.no = [];
              curTree = newTree;
            }
          });

          const programData = {instructions: instructionTree};
          // TODO(aaron): 'null' for now, but this will need to be type-subtype information relevant for the trace
          // TODO(jramli): 'false' for now, but this will need to be isGlobalCalcPlan flag relevant for the trace
          // This will be done when working on the tracing aspect of the calcs
          const program = Program.fromData(programData, sources, blocks, false, null, false);
          const chart = layoutProgram(program, this.gridSize, this.blockSizes);
          this.charts.push(chart);
          chart.nodes.forEach(n => {
            const x = programTrace.find(t => t.instruction.id === n.instruction.id);
            if (x) {
              n.traceInputs = x.instruction.inputs.map(ii => ii.trace);
              n.traceOutput = {resolved: x.output.resolved, value: formatOutputCell(x.output)};
            }
          });
          if (i === 0) {
            this.finalCalcOutput = "";
            if (chart.nodes.length) {
              const lastInstruction = programTrace[programTrace.length - 1].instruction;
              const lastNode = chart.nodesByInstructionId[lastInstruction.id];
              if (lastNode.instruction.block.type === BlockType.Value || lastNode.instruction.block.type === BlockType.End) {
                this.finalCalcOutput = lastNode.traceOutput.value;
              }
            }
          }
        });
        if (!this.charts.length) {
          this.traceNotAvailable = true;
        }
      }
    }
  }

  nodeTrackBy(index: number, node: TreeChartNode) {
    return node.instruction.id;
  }

  edgeTrackBy(index: number, edge: TreeChartEdge) {
    return edge.id;
  }

  selectTraceNode(chart: TreeChart, node: TreeChartNode) {
    const blockType = node.instruction.block.type;
    if (blockType === BlockType.Function ||
      blockType === BlockType.Decision ||
      blockType === BlockType.Value ||
      blockType === BlockType.Warning) {
      this.selectedChart = chart;
      this.selectedNode = node;
      setTimeout(() => this.scrollToNode(node), 0);
    }
  }

  getInputName(chart: TreeChart, node: TreeChartNode, calcInput: CalcInput) {
    const field: any = calcInput.field;
    if (calcInput.source === literalSource) {
      return "Literal";
    } else if (calcInput.source === calculationSource) {
      const resolved = chart.program.resolveIntermediateCalcInput(node.instruction, calcInput);
      const resolvedField: any = resolved.field;
      const fieldId = this.showQuestionId ? ` ${resolvedField.id}.` : ' ';
      return `${resolved.source.name} -${fieldId} ${resolvedField.name}`;
    } else {
      const fieldId = this.showQuestionId ? ` ${field.id}.` : ' ';
      return `${calcInput.source.name} -${fieldId} ${field.question}`;
    }
  }

  InputLinktoQuestion(calcInput: CalcInput) {
    if (calcInput.source.type === 'taxReturn' || calcInput.source.type === 'clientRules') {
      return true;
    } else {
      return false;
    }
  }

  navigateToQuestion(calcInput: CalcInput) {
    const field: any = calcInput.field;
    if (calcInput.source.type === 'taxReturn') {
      let InputQuestionSubcategory;
      this.taxReturn.categories.map(c => {
        c.subcategories.map(sc => {
          sc.groups.map(g=>{
            g.subgroups.map(sg=>{
              sg.questions.map(q=> {
                if (q.id === field.id) {
                  InputQuestionSubcategory = sc.id
                }
              })
            })
          })
        })
      })
      return {
        href: ['/tax-returns', this.taxReturn.id].concat('subcategories', InputQuestionSubcategory),
        query: {question: field.id}
      };
    }else {
      let InputClientRuleCategory;
      this.clientRules.populationClientRules.map(c => {
        c.clientQuestions.map(cq => {
          if (cq.id === field.id) {
            InputClientRuleCategory = cq.clientCategoryId;
          }
        })
      })
      return {
        href: ['/clients', this.clientRules.clientId].concat('populations', this.clientRules.id, 'rules'),
        query: {category: InputClientRuleCategory, taxYear: this.clientRules.year, question: field.id}
      };
    }
    
  }

  showViewAnswerModal(calcInput: CalcInput) {
    const field: any = calcInput.field;
    if (calcInput.source.type === 'taxReturn') {
      this.isClientRulesViewModal = false;
      this.taxReturn.categories.map(c => {
        c.subcategories.map(sc => {
          sc.groups.map(g=>{
            g.subgroups.map(sg=>{
              sg.questions.map(q=> {
                if (q.id === field.id) {
                  const currGroup = this.subCategory.groups.find((group: { id: any; }) => group.id === this.activeGroup.id);
                  if (!currGroup) {
                    this.group = this.subCategory.groups[0];
                  } else {
                    this.group = currGroup;
                  }
                  this.subGroup = this.group.subgroups.find(subgroup => subgroup.id = sg.id);
                  this.subCategoryName = sc.name;
                  this.inputQuestionId = field.id;
                }
              })
            })
          })
        })
      })
  
      this.group.formGroup = this.buildFormForGroup(this.group);
      this.group.uiControls = new UntypedFormGroup({
        'hideEmptyQuestions': new UntypedFormControl(false)
      });
      if (this.group.deleted) {
        this.group.deletedFormGroup = this.buildFormForGroup(this.group, true);
      }
    }else {
      this.isClientRulesViewModal = true;
      this.clientRules.populationClientRules.map(c => {
        c.clientQuestions.map(cq => {
          if (cq.displayFieldType === 'table') {

            cq.questions = [];
            // determine if tbl q is global
            cq.isGlobal = cq.clientAnswers && cq.clientAnswers.length > 0 && cq.clientAnswers[0].isGlobal;
  
            // find the parent table question and its column questions
            c.clientQuestions.forEach(_q => {
              if (_q.parentId === cq.id) {
                if (_q.displayFieldType === 'checkbox' && _q.dataType === 'boolean') {
                  cq.questions.unshift(_q);
                } else {
                  cq.questions.push(_q);
                }
              }
            });
  
            cq.answers = cq.clientAnswers.sort((a, b) => a.id - b.id);
            cq.rows = cq.clientAnswers.map((rowAnswer) => {
              const cellsByColQuestionId = {};
              cq.questions.forEach(colQ => {
                const cellAnswer = colQ.clientAnswers.find(a => a.parentClientAnswerId === rowAnswer.id);
                cellsByColQuestionId[colQ.id] = cellAnswer || {answer: ""};
              });
              return {
                rowAnswer,
                cells: cellsByColQuestionId
              };
            });
          }
          if (cq.id === field.id) {
            this.clientRulesCategory = c.name;
            this.categoryRules = c
            this.inputQuestionId = field.id
          }
        })
      })
      this.categoryRules.formGroup = this.buildFormForClientRules(this.clientRules)
    }

    $(this.viewAnswer.nativeElement).modal('show');
  }

  buildFormForGroup(group: IGroup, deleted: boolean = false) {
    const formGroup = new UntypedFormGroup({});
    const nonTableQuestions = [];
    const seenQuestionIds = new Set();
    if (group.question) {
      nonTableQuestions.push(group.question);
      seenQuestionIds.add(group.question.id);
    }

    group.subgroups.forEach(sg => {
      if (sg.isTable && !seenQuestionIds.has(sg.question.id)) {
        seenQuestionIds.add(sg.question.id);
        const colQuestions = sg.questions;
        colQuestions.forEach(colQ => {
          if (!seenQuestionIds.has(colQ.id)) {
            seenQuestionIds.add(colQ.id);
          }
        });
      }
    });
    group.subgroups.forEach(sg => {
      sg.questions.forEach(q => {
        if (!seenQuestionIds.has(q.id)) {
          nonTableQuestions.push(q);
          seenQuestionIds.add(q.id);
        }
      });
    });
    nonTableQuestions.forEach(q => {
      const control = makeControlForQuestion(q);
      formGroup.addControl(q.id, control);
      if (control.dirty && !formGroup.dirty) formGroup.markAsDirty();
    });
    return formGroup;
  }

  buildFormForClientRules(clientRules) {
    const formGroup = new UntypedFormGroup({});
    clientRules.populationClientRules.forEach(c => {

      c.clientQuestions.forEach(q => {

        // associate client specific models to generic models for form wrapper
        if (!q.clientAnswers) q['clientAnswers'] = [];
        q.answers = q.clientAnswers;
        if (q.clientQuestionDropdowns) q.questionDropdowns = q.clientQuestionDropdowns;

        // tb column questions added later
        if (q.displayFieldType !== 'table' && !q.parentId) {
          const control = makeControlForQuestion(q);
          if (control) {
            formGroup.addControl(q.id.toString(), control);
            if (control.dirty && !formGroup.dirty) formGroup.markAsDirty();
          }
        }
      });
    });
    return formGroup;
  }

  getTableCells(rowIndex: number, tableQ) {
    const row = tableQ.rows[rowIndex];
    const cells = tableQ.questions.filter(q => !q.hidden).map(columnQuestion => {
      const cellAnswer = row.cells[columnQuestion.id];
      const control = this.getFormControl(columnQuestion, rowIndex);
      const cell =  {
        question: columnQuestion,
        answer: cellAnswer,
        formControl: control,
        displayAnswer: ''
      };
      cell.displayAnswer = this.getDisplayAnswer(cell);
      return cell;
    });
    return cells;
  }

  getFormControl: Function = (q: IFormField, rowIndex?: number): AbstractControl => {
    return rowIndex != null ? this.categoryRules.formGroup.get(`${q.id}-${rowIndex}`) : this.categoryRules.formGroup.controls[q.id];
  };

  getDisplayAnswer(cell) {
    const q = cell.question;
    const answerValue = cell.formControl? cell.formControl.value : cell.answer.answer;
    if (q.displayFieldType === "drop down" || q.displayFieldType === "people" ) {
      const selectedDropdown = answerValue ? q.questionDropdowns.find(qd => qd.value === answerValue) : null;
      return selectedDropdown != null && selectedDropdown.display || answerValue;
    }
    return answerValue;
  }

  isBooleanCheckboxColumn(q: IFormField) {
    return q.displayFieldType === 'checkbox' && q.dataType === 'boolean';
  }

  
  selectCalcInput(chart: TreeChart, node: TreeChartNode, calcInput: CalcInput) {
    if (calcInput.source === calculationSource) {
      const resolved = chart.program.resolveIntermediateCalcInput(this.selectedNode.instruction, calcInput);
      const resolvedField: any = resolved.field;
      const n = chart.nodes.find(n => n.instruction.id === resolvedField.id);
      if (n) {
        this.selectTraceNode(chart, n);
      }
    }
  }

  isFinalOutput() {
    return this.selectedNode.instruction.block.type === BlockType.Value;
  }

  isEmpty(value) {
    return value == null || (value === "" && this.question.dataType !== "boolean");
  }

  scrollToNode(node: TreeChartNode) {
    const w = this.chartContainer.nativeElement.offsetWidth;
    const scrollLeft = this.chartContainer.nativeElement.scrollLeft;
    const left = node.rect.x - scrollLeft;
    const right = node.rect.x + node.rect.width - scrollLeft;

    if (left < 0 || right > w) {
      // not fully in view, scroll it in!
      const maxScroll = w - node.rect.width;
      const newLeft = Math.min(node.rect.x * 0.9, maxScroll);
      this.chartContainer.nativeElement.scrollLeft = newLeft;
    }
  }

  isArray(x) {
    return x instanceof Array;
  }

  formatFinalOutput(value) {
    if (this.question.isPercentage) {
      return `${Number(value) * 100}%`;
    } else if (this.question.isCurrency) {
      const n = Number(value);
      return Number.isNaN(n) ? value : this.currencyPipe.transform(value);
    } else if (this.question.dataType === "boolean" && value === "") {
      return "false";
    } else {
      return value;
    }
  }

  formatValue(value) {
    return value == null ? "null" : value;
  }
}
